import { useCallback, useMemo } from 'react';
import { getIn } from 'formik';
import { toast } from 'react-toastify';

import { POLICYHOLDER_ADDRESS_SAME_AS_MEMBER_FIELD_NAME } from './constants';
import { getPolicyholderSyncOverrides, hasAllRequiredMemberAddressFields } from './utils';

import type {
  InsuranceFieldName,
  InsuranceFieldValue,
  UsePolicyholderAddressSyncOptions,
} from './types';

export default function usePolicyholderAddressSync({
  formik,
  saveFieldOnBlur,
  saveInsuranceFields,
}: UsePolicyholderAddressSyncOptions) {
  const handleSameAsMemberChange = useCallback(
    async (name: InsuranceFieldName, value: InsuranceFieldValue) => {
      if (name !== POLICYHOLDER_ADDRESS_SAME_AS_MEMBER_FIELD_NAME) {
        return;
      }

      try {
        if (value !== true || !hasAllRequiredMemberAddressFields(formik.values)) {
          await saveFieldOnBlur(name, value);
          return;
        }

        const addressOverrides = getPolicyholderSyncOverrides(formik.values);

        addressOverrides.forEach(({ name: overrideName, value: overrideValue }) => {
          formik.setFieldValue(overrideName, overrideValue);
        });

        await saveInsuranceFields([{ name, value }, ...addressOverrides]);
      } catch {
        toast.error('Failed to sync policyholder address with member');
      }
    },
    [formik, saveFieldOnBlur, saveInsuranceFields],
  );

  const handleAddressFieldChange = useCallback(() => {
    if (!getIn(formik.values, POLICYHOLDER_ADDRESS_SAME_AS_MEMBER_FIELD_NAME)) {
      return;
    }
    formik.setFieldValue(POLICYHOLDER_ADDRESS_SAME_AS_MEMBER_FIELD_NAME, false);
  }, [formik]);

  const saveAddressFieldOnChange = useCallback(
    async (name: InsuranceFieldName, value: InsuranceFieldValue) => {
      if (!getIn(formik.values, POLICYHOLDER_ADDRESS_SAME_AS_MEMBER_FIELD_NAME)) {
        await saveFieldOnBlur(name, value);
        return;
      }

      formik.setFieldValue(POLICYHOLDER_ADDRESS_SAME_AS_MEMBER_FIELD_NAME, false);
      await saveInsuranceFields([
        { name, value },
        { name: POLICYHOLDER_ADDRESS_SAME_AS_MEMBER_FIELD_NAME, value: false },
      ]);
    },
    [formik, saveFieldOnBlur, saveInsuranceFields],
  );

  return useMemo(
    () => ({
      handleSameAsMemberChange,
      handleAddressFieldChange,
      saveAddressFieldOnChange,
    }),
    [handleSameAsMemberChange, handleAddressFieldChange, saveAddressFieldOnChange],
  );
}
